const KLAVIYO_API_REVISION = "2026-10-15";

async function postKlaviyo(url, payload) {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      accept: "application/vnd.api+json",
      revision: KLAVIYO_API_REVISION,
      "content-type": "application/vnd.api+json",
    },
    body: JSON.stringify(payload),
  });

  if (response.ok) return;

  let detail = "We couldn't save your request. Please try again.";
  try {
    const body = await response.json();
    detail = body?.errors?.[0]?.detail || detail;
  } catch (_) {
    // Klaviyo success responses have no body; retain the fallback for non-JSON errors.
  }

  throw new Error(detail);
}

function normalizePhone(value, dialCode) {
  const compact = value.trim().replace(/[^\d+]/g, "");
  if (compact.startsWith("+")) return /^\+[1-9]\d{7,14}$/.test(compact) ? compact : "";

  let digits = compact.replace(/\D/g, "");
  const dialDigits = dialCode.replace(/\D/g, "");
  if (!digits || !dialDigits) return "";

  if (dialDigits === "1") {
    if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
    if (digits.length === 10) return `+1${digits}`;
  }

  if (digits.startsWith(dialDigits) && digits.length >= 8) return `+${digits}`;
  digits = digits.replace(/^0+/, "");
  const phone = `+${dialDigits}${digits}`;
  return /^\+[1-9]\d{7,14}$/.test(phone) ? phone : "";
}

function validEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

class BisForm extends HTMLElement {
  constructor() {
    super();
    this.messageTimeout = null;
    this.form = this.querySelector("form");
    this.productId = this.dataset.productId || "";
    this.productForm = this.querySelector(
      `product-form[data-product-id="${this.productId}"], product-bundle-form[data-product-id="${this.productId}"]`,
    );
  }

  connectedCallback() {
    if (!this.form || !this.productForm || !this.productId) {
      console.error("BIS Form, Product Form, and Product ID are required");
      return;
    }

    this.form.setAttribute("novalidate", "novalidate");
    this.form.addEventListener("submit", (event) => {
      event.preventDefault();
      this.handleSubmit();
    });
  }

  get emailInput() {
    return this.form.querySelector('input[name="email"]');
  }

  get phoneInput() {
    return this.form.querySelector('input[name="phone-number"]');
  }

  get countrySelect() {
    return this.form.querySelector('select[name="phone-country"]');
  }

  async handleSubmit() {
    const data = new FormData(this.form);
    const email = String(data.get("email") || "").trim();
    const rawPhone = String(data.get("phone-number") || "").trim();
    const dialCode = String(data.get("phone-country") || "").trim();
    const phone = normalizePhone(rawPhone, dialCode);
    const smsConsent = data.get("sms-consent") === "accept-sms";
    const emailMarketing = data.get("marketing") === "accept-marketing";
    const listId = String(data.get("g") || "").trim();
    const publicApiKey = String(data.get("api-key") || "").trim();
    const propertyKey = String(data.get("property-key") || "").trim();
    const propertyValue = String(data.get("property-value") || "").trim();
    const variantId = this.productForm.getSelectedVariantId();

    this.hideMessages();

    if (!email && !rawPhone) {
      this.showMessage("Enter an email address, a mobile number, or both.", "error", this.emailInput);
      return;
    }
    if (email && !validEmail(email)) {
      this.showMessage("Enter a valid email address.", "error", this.emailInput);
      return;
    }
    if (rawPhone && !phone) {
      this.showMessage("Choose your country and enter a valid mobile number.", "error", this.phoneInput);
      return;
    }
    if (phone && !smsConsent) {
      this.showMessage("Please accept the SMS consent statement to receive text alerts.", "error", this.phoneInput);
      return;
    }
    if (!publicApiKey || !variantId) {
      this.showMessage("This product isn't ready for notifications yet. Please try again later.", "error", this.emailInput);
      return;
    }

    const submitButton = this.form.querySelector('button[type="submit"]');
    const profileAttributes = {};
    const channels = [];

    if (email) {
      profileAttributes.email = email;
      channels.push("EMAIL");
    }
    if (phone) {
      profileAttributes.phone_number = phone;
      channels.push("SMS");
    }
    if (propertyKey && propertyValue) {
      profileAttributes.properties = { [propertyKey]: propertyValue };
    }

    const endpointQuery = `company_id=${encodeURIComponent(publicApiKey)}`;
    const backInStockPayload = {
      data: {
        type: "back-in-stock-subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: profileAttributes,
            },
          },
          channels,
        },
        relationships: {
          variant: {
            data: {
              type: "catalog-variant",
              id: `$shopify:::$default:::${variantId}`,
            },
          },
        },
      },
    };

    const subscriptions = {};
    if (email && emailMarketing) {
      subscriptions.email = { marketing: { consent: "SUBSCRIBED" } };
    }
    if (phone) {
      subscriptions.sms = { marketing: { consent: "SUBSCRIBED" } };
    }

    try {
      this.setDisabled(true, submitButton);

      if (listId && Object.keys(subscriptions).length) {
        const consentPayload = {
          data: {
            type: "subscription",
            attributes: {
              profile: {
                data: {
                  type: "profile",
                  attributes: {
                    ...profileAttributes,
                    subscriptions,
                  },
                },
              },
              custom_source: "Back in Stock",
            },
            relationships: {
              list: {
                data: { type: "list", id: listId },
              },
            },
          },
        };

        await postKlaviyo(`https://a.klaviyo.com/client/subscriptions/?${endpointQuery}`, consentPayload);
      }

      await postKlaviyo(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?${endpointQuery}`,
        backInStockPayload,
      );

      window.Toaster?.show({
        type: "success",
        message: "You're on the list. We'll notify you when this item is back in stock.",
      });
      this.form.reset();
    } catch (error) {
      window.Toaster?.show({ type: "error", message: error.message });
      this.showMessage(error.message, "error", this.emailInput);
    } finally {
      this.setDisabled(false, submitButton);
    }
  }

  setDisabled(disabled, submitButton) {
    [this.emailInput, this.phoneInput, this.countrySelect, submitButton].forEach((element) => {
      if (element) element.disabled = disabled;
    });
  }

  showMessage(message, type, input) {
    this.hideMessages();
    const element = document.createElement("div");
    element.className = `message ${type}`;
    element.textContent = message;
    (input || this.emailInput)?.insertAdjacentElement("afterend", element);
    (input || this.emailInput)?.classList.add("input-error");
    (input || this.emailInput)?.focus();
    this.messageTimeout = window.setTimeout(() => this.hideMessages(), 7000);
  }

  hideMessages() {
    if (this.messageTimeout) window.clearTimeout(this.messageTimeout);
    this.messageTimeout = null;
    this.querySelectorAll(".message").forEach((element) => element.remove());
    [this.emailInput, this.phoneInput].forEach((input) => input?.classList.remove("input-error"));
  }
}

if (!customElements.get("bis-form")) customElements.define("bis-form", BisForm);
